type HasItems<Type> = Omit<Type, 'items'> & {
  items?: HasItems<Type>[];
};

export function treeTransform<TIn extends { items?: TIn[] }, TOut extends HasItems<TOut>>(
  tree: TIn[],
  transform: (item: TIn) => TOut
): HasItems<TOut>[] {
  return tree.map((item: TIn): HasItems<TOut> => {
    const newItem = transform(item);
    if (item.items === undefined) {
      return newItem;
    } else {
      return { ...newItem, items: treeTransform<TIn, TOut>(item.items, transform) };
    }
  });
}

export function treeFilter<TIn extends { items?: TIn[] }>(tree: TIn[], filter: (item: TIn) => boolean): TIn[] {
  return tree
    .map((item: TIn): TIn => {
      const { ...newItem } = item;
      if (item.items !== undefined) {
        newItem.items = treeFilter<TIn>(item.items, filter);
      }

      return newItem;
    })
    .filter(filter);
}

export function isTreeNodeWithChilds<TIn extends { items?: TIn[] }>(item: TIn): boolean {
  return item.items !== undefined && item.items.length !== 0;
}

export function treeCount<TIn extends { items?: TIn[] }>(
  tree: TIn[],
  filter: (item: TIn) => boolean = () => true
): number {
  return tree
    .map((item) => {
      let result = filter(item) ? 1 : 0;
      if (item.items !== undefined) {
        result += treeCount(item.items, filter);
      }
      return result;
    })
    .reduce((a, b) => a + b, 0);
}

export function getFolderIds<TIn extends { items?: TIn[]; id: string }>(tree: TIn[]): string[] {
  const result: string[] = [];
  for (const item of tree) {
    if (item.items !== undefined) {
      result.push(item.id);
      result.push(...getFolderIds(item.items));
    }
  }
  return result;
}

export function allIds<TIn extends { items?: TIn[]; id: string }>(tree: TIn[]): string[] {
  const result: string[] = [];
  for (const item of tree) {
    result.push(item.id);
    if (item.items !== undefined) {
      result.push(...allIds(item.items));
    }
  }
  return result;
}

export function getLeafIds<TIn extends { items?: TIn[]; id: string }>(tree: TIn[]): Array<string> {
  return tree.flatMap((item) => (item.items === undefined ? [item.id] : getLeafIds(item.items ?? [])));
}

export function treeLeafs<TIn extends { items?: TIn[]; id: string }>(tree: TIn[]): Record<string, TIn> {
  const flatList = (nodes: TIn[]): [string, TIn][] =>
    nodes.flatMap((item) => (item.items === undefined ? [[item.id, item]] : flatList(item.items ?? [])));
  return Object.fromEntries(flatList(tree));
}

export function nodesToItems<TIn extends { nodes: TIn[] | null }>(tree: TIn[]): HasItems<Omit<TIn, 'nodes'>>[] {
  return tree.map((item) => {
    const { nodes, ...rest } = item;
    return { items: nodes !== null ? nodesToItems(nodes) : undefined, ...rest };
  });
}

export function findById<TIn extends { items?: TIn[]; id: string }>(tree: TIn[], id: string): TIn | undefined {
  for (const item of tree) {
    if (item.id === id) {
      return item;
    }
    if (item.items !== undefined) {
      const childResult = findById(item.items, id);
      if (childResult !== undefined) {
        return childResult;
      }
    }
  }
}

function findNonUniqueIds<TIn extends { items?: TIn[]; id: string }>(tree: TIn[]): Set<string> {
  const usedIds = new Set<string>();
  const usedNonUniqueIds = new Set<string>();

  function find(nodes: TIn[]) {
    for (const node of nodes) {
      const id = node.id;
      if (id !== null) {
        if (usedIds.has(id)) {
          usedNonUniqueIds.add(id);
        }
        usedIds.add(id);
      }
      find(node.items ?? []);
    }
  }
  find(tree ?? []);

  return usedNonUniqueIds;
}

export function makeIdsUnique<TIn extends { items?: TIn[]; id: string }>(tree: TIn[]): TIn[] {
  const usedNonUniqueIds = findNonUniqueIds(tree);

  let index = 0;

  return treeTransform(tree, (item) => {
    let newId = item.id;
    if (item.id === null || usedNonUniqueIds.has(item.id)) {
      index += 1;
      newId = `${item.id}:client_${index}`;
    }
    return { ...item, id: newId };
  }) as TIn[];
}
